/* eslint-disable max-lines, max-statements */
import {omit, path, prop} from 'ramda';

import type {CategoryProductItemType} from 'constants/flow/flowTypesCategory';
import type {FilterItemType, SimpleProductType} from 'constants/flow/flowTypes';

import type {ActiveFiltersType} from 'reducers/category/activeFilters/activeFiltersType';
import {createUrlString, getUrlParams} from 'libs/url';
import {logWithSentry} from 'libs/sentry/sentry';
import {getMsMediaSourceUrl} from 'libs/utils/utils.js';
import {ReturnDataCategoryItemType} from '../../actions/category/category';
import {API_URL, HTTP_REQUEST_HEADER} from '../../constants/api';
import {instanceGusaBackend} from '../api';
import {FetchCategoryMsResponseType} from '../../reducers/category/categoryItemsType';
import {AxiosPromise} from 'axios';
import {CATEGORY} from '../../constants/actions';
import {CATEGORY_PRODUCT_LIMIT} from '../../constants/category';

/**
 * Get image URL of category item from elastic ms endpoint
 * @param {CategoryProductItemType} categoryItem - category item
 * @param {'front' | 'angle' | 'side'} mediaType - media type
 * @return {string} URL of the image
 */
export const getItemImageElastic = (categoryItem, mediaType = 'front') => {
    const imageUrl = path(['media', mediaType, 'src'], categoryItem);

    return imageUrl ? getMsMediaSourceUrl(`${imageUrl}/h=310,w=620,fit=fill,bg=fff`) : '';
};

/**
 * Template for keys replacement of category item data
 * @type {{regular_price_with_tax: string, final_price_with_tax: string}}
 */
const productKeys = {regular_price_with_tax: 'price', final_price_with_tax: 'final_price'};

/**
 * Updates key names. This function used to align category item's data structure to product page's data structure.
 * @param {SimpleProductType} simpleProduct - category item data
 * @param {{string: string}} newKeys - list of keys to update
 * @returns {SimpleProductType} structure that ready for product page
 */
export const renameKeys = (
    simpleProduct: SimpleProductType,
    newKeys: {[string]: string} = productKeys,
): SimpleProductType => {
    const keyValues = Object.keys(simpleProduct).map((key: string): string => {
        const newKey = newKeys[key] || key;
        return {[newKey]: simpleProduct[key]};
    });

    return Object.assign({}, ...keyValues);
};

/**
 * Get image URL of category item
 * @param {CategoryProductItemType} categoryItem - category item
 * @return {string} URL of the image
 */
export const getCategoryItemImage = (categoryItem: CategoryProductItemType): string => {
    const image = path(['image_gallery', '0', 'catalog/product/view', 'image'], categoryItem);

    if (!image) {
        logWithSentry(
            new Error(
                `Image for ${prop('name', categoryItem)} (SKU ${prop(
                    'sku',
                    categoryItem,
                )}) is missed`,
            ),
        );
    }

    return image || '';
};

/**
 * Get image URL of category item from elastic ms endpoint
 * @param {CategoryProductItemType} categoryItem - category item
 * @param {'front' | 'angle' | 'side'} mediaType - media type
 * @return {string} URL of the image
 */
export const getCategoryItemImageElastic = (
    categoryItem: CategoryProductItemType,
    mediaType: 'front' | 'angle' | 'side' = 'front',
): string => {
    const imageUrl = path(['media', mediaType, 'src'], categoryItem);

    if (!imageUrl) {
        logWithSentry(
            new Error(
                `Image for ${prop('name', categoryItem)} (SKU ${prop(
                    'sku',
                    categoryItem,
                )}) is missed`,
            ),
        );

        return '';
    }

    return getMsMediaSourceUrl(`${imageUrl}/h=310,w=620,fit=fill,bg=fff`);
};

/**
 * Prepare images data structure for product page.
 * @param {SimpleProductType} simpleProduct - category item data
 * @return {SimpleProductType} contains image's data structure that ready for product page
 */
export const extractImages = (simpleProduct: SimpleProductType): SimpleProductType => ({
    ...simpleProduct,
    images: [getCategoryItemImage(simpleProduct)],
});

/**
 * Returns active filters from url
 * @param {FilterItemType} filterItems - possible filters
 * @param {string} searchParams - URL search parameters
 * @return {ActiveFiltersType} active filters
 */
export const getActiveFiltersFromUrl = (
    filterItems: Array<FilterItemType>,
    searchParams?: string,
): ActiveFiltersType => {
    const activeFilters = getUrlParams(searchParams);
    if (activeFilters.p) delete activeFilters.p;

    const filterList = {};

    Object.keys(activeFilters).forEach(code => {
        const filterItem = (filterItems || []).find(({code: filterCode}) => filterCode === code);
        if (filterItem)
            filterList[code] = filterItem.items
                .filter(
                    ({value, value_id: id}) =>
                        activeFilters[code].includes(value) ||
                        activeFilters[code].includes(id.toString()),
                )
                .map(({title, value, value_id: id}) => ({title, value, value_id: id}));
    });

    return filterList;
};

export const THE_MAXIMUM_AMOUNT_OF_CHILDREN_IN_ONE_WINDOW_PRODUCT = 8;

/**
 * Generates search string for filters
 * @param {ActiveFilterType} params - Active filters
 * @param {ReturnDataCategoryItemType} data - metadata
 * @param {number} categoryId - categoryId
 * @returns {string} search string
 */
export function transformFiltersToSearchString(
    params: ActiveFiltersType,
    data: ReturnDataCategoryItemType,
) {
    const {algoliaParams = {}, otherParams = {}, page, dir} = data;

    const otherParamsObj = __NODEJS__ ? otherParams : getUrlParams();

    // eslint-disable-next-line no-param-reassign
    if (params.p) delete params.p;

    const filters = Object.keys(params)
        .filter(code => ['tab', 'frame'].indexOf(code) === -1 && params[code]?.length)
        // eslint-disable-next-line @typescript-eslint/naming-convention,camelcase
        .map(code => `${code}=${params[code].map(({value_id}) => value_id).join(',')}`)
        .join('&');

    const {order} = data;

    const urlParams = {
        ...omit(['frame', 'tab', 'p', 'dir', 'order', ...Object.keys(params)], otherParamsObj),
        ...algoliaParams,
        limit: [CATEGORY_PRODUCT_LIMIT],
        offset: [page ? CATEGORY_PRODUCT_LIMIT * (page - 1) : 0],
        colorLimit: THE_MAXIMUM_AMOUNT_OF_CHILDREN_IN_ONE_WINDOW_PRODUCT,
        ...(dir
            ? {
                  dir: [dir],
              }
            : {}),
        ...(order
            ? {
                  order: [order],
              }
            : {}),
    };

    return `${createUrlString(urlParams)}${filters && `&${filters}`}`;
}

/**
 * Creates redux action for fetching category items
 * @param {categoryId} categoryId - id of category
 * @param {string} url - url
 * @param {ReturnDataCategoryItemType} data -  meta data
 * @returns {ActionType<AxiosPromise>} Action
 */
export const generateActionForCategoryFetch = (
    categoryId: number,
    url: string,
    data: ReturnDataCategoryItemType,
): ActionType<
    {promise: AxiosPromise<FetchCategoryMsResponseType>, data: {id: number, isAppend: boolean}},
    {currentlyRequiredPage: string | number},
> => {
    return {
        type: CATEGORY.GET_CATEGORY_ITEMS,
        payload: {
            promise: instanceGusaBackend({
                method: 'get',
                baseURL: API_URL,
                headers: {
                    [HTTP_REQUEST_HEADER.ACCEPT_ENCODING]: 'gzip',
                },
                url,
                cancelToken: data.cancelToken,
            }),
            data: {
                id: categoryId,
                isAppend: data.append,
            },
        },
        meta: {currentlyRequiredPage: data.page},
    };
};
